import { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';

// Get the repository instance
function getRepository() {
  if (typeof window !== 'undefined' && (window as any).__expenseRepository) {
    return (window as any).__expenseRepository;
  }
  return null;
}

export interface SyncStatus {
  pending: number;
  failed: number;
  synced: number;
}

/**
 * Hook to get sync status - isolated to prevent unnecessary re-renders
 * Only components that need sync status will re-render when it updates
 */
export function useSyncStatusPolling() {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    pending: 0,
    failed: 0,
    synced: 0
  });

  useEffect(() => {
    const repository = getRepository();
    if (!repository) {
      logger.debug('[useSyncStatusPolling] Repository not available yet');
      return;
    }

    const updateStatus = async () => {
      try {
        const stats = await repository.getStats();
        setSyncStatus({
          pending: stats.pending,
          failed: stats.failed,
          synced: stats.synced
        });
      } catch (error) {
        logger.error('[useSyncStatusPolling] Failed to get stats:', { error });
      }
    };

    // Initial update
    updateStatus();

    // Poll every 5 seconds - only this hook and components using it will re-render
    const interval = setInterval(updateStatus, 5000);

    return () => clearInterval(interval);
  }, []);

  return syncStatus;
}